import type { PortalAssemblyTask, PortalForecastRow } from '@proerp/shared';
import { formatQuantity } from './formatForecast';

/**
 * Kennzahlen und Zeitreihen des Partnerportals (ADR 0085).
 *
 * Bewusst reine Funktionen ohne React: Was eine Kachel behauptet und was ein
 * Diagramm zeigt, ist eine fachliche Aussage — die soll ohne DOM prüfbar sein.
 *
 * **Die Einheit ist hier der springende Punkt.** Über Artikel hinweg lassen
 * sich Mengen nicht addieren: „500 Stk + 40 kg = 540" ist keine Zahl, sondern
 * ein Fehler. Deshalb summiert dieses Modul Mengen ausschließlich **je
 * Einheit** und gibt sie auch so aus („500 Stk · 40 kg"). Ein einzelner
 * Gesamtwert für „Menge" existiert bewusst nicht.
 */

/** Zeilen ohne eigene Einheit brauchen trotzdem einen Topf. */
export const UNIT_FALLBACK = 'ohne Einheit';

/**
 * Ab wie vielen Monaten Spannweite die Lücken NICHT mehr aufgefüllt werden.
 * Der Zeithorizont ist ungedeckelt (Tech-Lead 2026-08-06) — bei einer Zeile in
 * 2026 und einer in 2034 entstünden sonst über hundert leere Balken, in denen
 * die zwei echten untergehen.
 */
const MAX_FILLED_MONTHS = 36;

const MONTH_LABEL = new Intl.DateTimeFormat('de-DE', {
  month: 'short',
  year: '2-digit',
  timeZone: 'Europe/Berlin',
});

/**
 * Mengen je Einheit als ein Text: „500 Stk · 40 kg". Sortiert nach Menge
 * absteigend, damit das Wesentliche vorne steht.
 */
export function formatQuantityByUnit(byUnit: ReadonlyMap<string, number>): string {
  if (byUnit.size === 0) return '—';
  return [...byUnit.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([unit, quantity]) =>
      unit === UNIT_FALLBACK ? formatQuantity(quantity) : `${formatQuantity(quantity)} ${unit}`,
    )
    .join(' · ');
}

/** `YYYY-MM` in Europe/Berlin — Schlüssel für die Monatsgruppierung. */
export function monthKey(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  // `sv-SE` liefert ISO-artige Datumsangaben (`2026-03-01`) — der kürzeste
  // Weg zu einem sortierbaren Monatsschlüssel in der richtigen Zeitzone.
  const [year, month] = date
    .toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' })
    .split('-');
  return `${year}-${month}`;
}

/** Beschriftung eines Monatsschlüssels für die Achse („Mär 26"). */
export function monthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return MONTH_LABEL.format(new Date(Date.UTC(year, (month ?? 1) - 1, 15)));
}

// ---- Bedarfsvorschau ---------------------------------------------------------

/** Ein Balken: Monat plus je Einheit eine Menge. */
export type DemandPoint = Record<string, string | number>;

export interface DemandSeries {
  /** Vorkommende Einheiten, stabil sortiert — je Einheit ein Balkensegment. */
  units: string[];
  points: DemandPoint[];
  /** Zeilen ohne Termin passen in keinen Monat und werden separat genannt. */
  undatedCount: number;
}

/**
 * Bedarf je Monat, **gestapelt nach Einheit**.
 *
 * Die Stapelung ist keine Optik, sondern die einzige ehrliche Art, gemischte
 * Einheiten in einem Diagramm zu zeigen: Jede Einheit bekommt ihr eigenes
 * Segment, nichts wird über Einheiten hinweg addiert.
 *
 * Lücken zwischen dem ersten und letzten Monat werden mit Nullen gefüllt —
 * sonst stünden März und Dezember nebeneinander und die Zeitachse würde lügen.
 * Bei sehr weitem Horizont unterbleibt das (siehe `MAX_FILLED_MONTHS`).
 */
export function buildDemandSeries(rows: readonly PortalForecastRow[]): DemandSeries {
  const byMonth = new Map<string, Map<string, number>>();
  const units = new Set<string>();
  let undatedCount = 0;

  for (const row of rows) {
    if (!row.expectedDate) {
      undatedCount += 1;
      continue;
    }
    const key = monthKey(row.expectedDate);
    if (!key) {
      undatedCount += 1;
      continue;
    }
    const unit = row.unit?.trim() || UNIT_FALLBACK;
    units.add(unit);
    const bucket = byMonth.get(key) ?? new Map<string, number>();
    bucket.set(unit, (bucket.get(unit) ?? 0) + row.quantity);
    byMonth.set(key, bucket);
  }

  const keys = fillMonthGaps([...byMonth.keys()].sort());
  const unitList = [...units].sort((a, b) => a.localeCompare(b, 'de'));
  const points = keys.map((key) => {
    const bucket = byMonth.get(key);
    const point: DemandPoint = { month: key, label: monthLabel(key) };
    for (const unit of unitList) point[unit] = bucket?.get(unit) ?? 0;
    return point;
  });

  return { units: unitList, points, undatedCount };
}

/** Füllt fehlende Monate zwischen erstem und letztem Schlüssel auf. */
function fillMonthGaps(sortedKeys: readonly string[]): string[] {
  if (sortedKeys.length < 2) return [...sortedKeys];
  const [firstYear, firstMonth] = sortedKeys[0].split('-').map(Number);
  const [lastYear, lastMonth] = sortedKeys[sortedKeys.length - 1].split('-').map(Number);
  const span = (lastYear - firstYear) * 12 + (lastMonth - firstMonth) + 1;
  if (span > MAX_FILLED_MONTHS) return [...sortedKeys];

  const filled: string[] = [];
  for (let index = 0; index < span; index += 1) {
    const total = (firstMonth - 1) + index;
    const year = firstYear + Math.floor(total / 12);
    const month = (total % 12) + 1;
    filled.push(`${year}-${String(month).padStart(2, '0')}`);
  }
  return filled;
}

export interface ForecastKpis {
  /** Zeilen der Vorschau (Artikel × Termin × Projekt). */
  positionCount: number;
  articleCount: number;
  /** Verschiedene Projekte — seit 2026-08-07 sichtbar (ADR 0085). */
  projectCount: number;
  /** Frühester Termin als ISO-String; `null`, wenn alles terminlos ist. */
  nextDate: string | null;
  /** Mengen zum frühesten Termin, je Einheit. */
  nextByUnit: Map<string, number>;
}

/**
 * Die Kopfzahlen der Übersicht. `nextDate` ist die Zahl, für die ein Lieferant
 * die Seite überhaupt öffnet: „was kommt als Nächstes".
 */
export function forecastKpis(rows: readonly PortalForecastRow[]): ForecastKpis {
  const articles = new Set<string>();
  const projects = new Set<string>();
  let nextDate: string | null = null;

  for (const row of rows) {
    articles.add(row.articleId);
    if (row.projectText) projects.add(row.projectText);
    if (row.expectedDate && (nextDate === null || row.expectedDate < nextDate)) {
      nextDate = row.expectedDate;
    }
  }

  const nextByUnit = new Map<string, number>();
  for (const row of rows) {
    if (row.expectedDate !== nextDate) continue;
    const unit = row.unit?.trim() || UNIT_FALLBACK;
    nextByUnit.set(unit, (nextByUnit.get(unit) ?? 0) + row.quantity);
  }

  return {
    positionCount: rows.length,
    articleCount: articles.size,
    projectCount: projects.size,
    nextDate,
    nextByUnit,
  };
}

export interface ArticleOption {
  articleId: string;
  /** „A-100 · Sechskantschraube M12" — Nummer zuerst, danach gesucht wird. */
  label: string;
  unit: string | null;
}

/** Die Artikel der Vorschau für die Auswahl im Verlaufs-Diagramm. */
export function articleOptions(rows: readonly PortalForecastRow[]): ArticleOption[] {
  const byId = new Map<string, ArticleOption>();
  for (const row of rows) {
    if (byId.has(row.articleId)) continue;
    byId.set(row.articleId, {
      articleId: row.articleId,
      label: row.articleNumber ? `${row.articleNumber} · ${row.description}` : row.description,
      unit: row.unit?.trim() || null,
    });
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, 'de'));
}

export interface ArticlePoint {
  month: string;
  label: string;
  quantity: number;
}

/**
 * Mengenverlauf EINES Artikels. Innerhalb eines Artikels ist die Einheit
 * konstant — deshalb darf hier, anders als in der Gesamtsicht, zu einer
 * einzigen Zahl je Monat summiert werden.
 */
export function articleSeries(
  rows: readonly PortalForecastRow[],
  articleId: string,
): ArticlePoint[] {
  const byMonth = new Map<string, number>();
  for (const row of rows) {
    if (row.articleId !== articleId || !row.expectedDate) continue;
    const key = monthKey(row.expectedDate);
    if (!key) continue;
    byMonth.set(key, (byMonth.get(key) ?? 0) + row.quantity);
  }
  return fillMonthGaps([...byMonth.keys()].sort()).map((key) => ({
    month: key,
    label: monthLabel(key),
    quantity: byMonth.get(key) ?? 0,
  }));
}

// ---- Montage -----------------------------------------------------------------

export interface AssemblyKpis {
  /** Alles, was weder abgeschlossen noch abgesagt ist. */
  openCount: number;
  doneCount: number;
  /** Nächster geplanter Beginn eines offenen Einsatzes. */
  nextStart: string | null;
  /** Offene Einsätze ohne Termin — die brauchen eine Rückfrage. */
  undatedCount: number;
}

export function assemblyKpis(tasks: readonly PortalAssemblyTask[]): AssemblyKpis {
  let openCount = 0;
  let doneCount = 0;
  let undatedCount = 0;
  let nextStart: string | null = null;

  for (const task of tasks) {
    if (task.status === 'DONE' || task.status === 'CANCELLED') {
      doneCount += 1;
      continue;
    }
    openCount += 1;
    if (!task.plannedStart) {
      undatedCount += 1;
      continue;
    }
    if (nextStart === null || task.plannedStart < nextStart) nextStart = task.plannedStart;
  }

  return { openCount, doneCount, nextStart, undatedCount };
}
